import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import EmailIcon from "@mui/icons-material/Email";
import GitHubIcon from "@mui/icons-material/GitHub";
import YouTubeIcon from "@mui/icons-material/YouTube";
import Footer from "../components/Footer";
import { updateMetaTags } from "../utils/seo";

const Contact: React.FC = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    updateMetaTags({
      title: "Contact - Shashi Sharma",
      description:
        "Get in touch with Shashi Sharma — Flutter developer and tech blogger. Send me a message or connect on GitHub and YouTube.",
      keywords:
        "contact, shashi sharma, flutter developer, get in touch, email",
      canonical: `${window.location.origin}/contact`,
      ogTitle: "Contact Shashi Sharma",
      ogDescription:
        "Get in touch with Shashi Sharma — Flutter developer and tech blogger.",
      ogUrl: `${window.location.origin}/contact`,
      ogType: "website",
      twitterCard: "summary",
      twitterTitle: "Contact Shashi Sharma",
      twitterDescription:
        "Get in touch with Shashi Sharma — Flutter developer and tech blogger.",
    });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mailtoLink = `mailto:sharmashashi940@gmail.com?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(message)}`;
    window.location.href = mailtoLink;
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Get In Touch
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: "text.secondary", textAlign: "center" }}
          >
            Have a question, idea, or just want to say hi? I'd love to hear from
            you.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6, flex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 6,
          }}
        >
          {/* Contact Form */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Send a Message
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What's this about?"
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Message"
                multiline
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Your message..."
                required
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                endIcon={<SendIcon />}
              >
                Send Message
              </Button>
            </Box>
          </Box>

          {/* Contact Info */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Other Ways to Connect
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Card variant="outlined">
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <EmailIcon sx={{ color: "primary.main", fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Email
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="a"
                      href="mailto:sharmashashi940@gmail.com"
                      sx={{
                        textDecoration: "none",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      sharmashashi940@gmail.com
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <GitHubIcon sx={{ color: "primary.main", fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      GitHub
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="a"
                      href="https://github.com/sharmashashi"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textDecoration: "none",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      github.com/sharmashashi
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <YouTubeIcon sx={{ color: "#c31432", fontSize: 28 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      YouTube
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="a"
                      href="https://www.youtube.com/@_shashisharma"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textDecoration: "none",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      @_shashisharma
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Box
                sx={{
                  mt: 2,
                  p: 3,
                  backgroundColor: "rgba(145, 189, 58, 0.06)",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "rgba(145, 189, 58, 0.2)",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.8 }}
                >
                  I typically respond within 1–2 business days. For
                  collaboration proposals, bug reports, or general questions
                  about any of my articles or projects — feel free to reach out
                  anytime.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default Contact;
